import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const CATEGORIES = [
  {
    name: "Industrial Hardware",
    description:
      "Heavy-duty tools, fasteners and machinery components sourced to specification.",
    examples: "Power tools · Fasteners · Machine parts",
  },
  {
    name: "Commercial Equipment",
    description:
      "Reliable equipment for offices, retail floors and operational facilities.",
    examples: "Office systems · Retail fixtures · Facility gear",
  },
  {
    name: "Technical Supplies",
    description:
      "Precision instruments and technical consumables for professional operations.",
    examples: "Instruments · Consumables · Safety gear",
  },
  {
    name: "Custom Solutions",
    description:
      "Bespoke procurement — tell us what you need and we source it for you.",
    examples: "Bulk orders · Special requests · Branded supply",
  },
];

export default function CategoryShowcase({ onSelect }) {
  return (
    <section
      id="showcase"
      data-testid="showcase-section"
      className="py-16 lg:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl mb-12"
        >
          <span className="eyebrow" data-testid="showcase-eyebrow">
            What We Supply
          </span>
          <h2 className="mt-4 font-poppins font-semibold text-2xl sm:text-3xl text-charcoal-900">
            Our Product Categories
          </h2>
          <p className="mt-4 text-sm sm:text-base text-charcoal-700 leading-relaxed">
            Select a category to begin your order — or simply tell us exactly
            what you require in the order form.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-5">
          {CATEGORIES.map((cat, i) => (
            <motion.div
              key={cat.name}
              data-testid={`category-card-${i}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group bg-white border border-cream-200 rounded-xl p-6 sm:p-7 hover:border-taupe-400 transition-colors duration-200"
            >
              <div className="flex items-start justify-between gap-4">
                <h3 className="font-poppins font-semibold text-lg text-charcoal-900">
                  {cat.name}
                </h3>
                <span className="text-[11px] font-medium tracking-[0.15em] text-taupe-500 mt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <p className="mt-3 text-sm text-charcoal-700 leading-relaxed">
                {cat.description}
              </p>
              <p className="mt-3 text-[11px] uppercase tracking-[0.15em] text-taupe-600">
                {cat.examples}
              </p>
              <button
                data-testid={`category-select-button-${i}`}
                onClick={() => onSelect(cat.name)}
                className="mt-5 group/btn flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-medium text-charcoal-900 hover:text-taupe-700 transition-colors duration-200"
              >
                Select for Order
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
